import { useMemo, useState } from "react";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";
import { OBJECT_TYPE, REGEX } from "~/core/constants/common";
import { useInvalidateGlobalObjectSearch } from "~/core/hooks/objects/useGlobalSearch";

import { SearchObjectsResponse } from "~/components/GlobalCommandbar/types/common";

import { createPeople } from "~/lib/database/people-data";

import { AccountLabel } from "../components/FormItems/AccountLabel";
import { useGlobalNewOpportunityModalContext } from "../context";
import { useGlobalCreateOpportunityStore } from "../store";
import { DDRendererItem } from "../types";

export const useAddContact = (inputValue: string, isMultiContact: boolean) => {
  const invalidateGlobalObjectSearch = useInvalidateGlobalObjectSearch();

  const [addingNewContact, setAddingNewContact] = useState(false);

  const setContacts = useGlobalCreateOpportunityStore(
    state => state.setContacts,
  );
  const setSelectedContact = useGlobalCreateOpportunityStore(
    state => state.setSelectedContact,
  );
  const setMultiSelectContacts = useGlobalCreateOpportunityStore(
    state => state.setMultiSelectContacts,
  );
  const multiSelectContacts = useGlobalCreateOpportunityStore(
    state => state.multiSelectContacts,
  );

  const isValidEmail = useMemo(() => {
    return REGEX.EMAIL.test(inputValue);
  }, [inputValue]);

  const { form } = useGlobalNewOpportunityModalContext();

  const handleOnAddContact = async () => {
    if (!isValidEmail) return;
    setAddingNewContact(true);

    try {
      let first_name = inputValue.split("@")[0];
      first_name = first_name.charAt(0).toUpperCase() + first_name.slice(1);
      const { data } = await createPeople({ email: inputValue, first_name });

      if (data.data) {
        invalidateGlobalObjectSearch();

        const newContactData: SearchObjectsResponse = {
          object_id: data.data.id,
          description: data.data.email,
          title: data.data.first_name,
          image_url: data.data.avatar_url,
          object_type: OBJECT_TYPE.CONTACT,
        };
        const structuredContactData: DDRendererItem = {
          id: newContactData.object_id,
          label: <AccountLabel item={newContactData} />,
          active: true,
          searchableText: newContactData.title || newContactData.description,
          metadata: {
            description: newContactData.description,
            title: newContactData.title || newContactData.description,
            image_url: newContactData.image_url,
          },
        };
        setContacts([structuredContactData]);
        setTimeout(() => {
          if (!isMultiContact) {
            form?.setFieldsValue({
              [SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_CONTACT]:
                newContactData.object_id,
            });
            setSelectedContact(structuredContactData);
          } else {
            setMultiSelectContacts([
              ...multiSelectContacts,
              structuredContactData,
            ]);

            form?.setFieldsValue({
              [SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS]: [
                ...(form?.getFieldValue(
                  SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
                ) || []),
                newContactData.object_id,
              ],
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error(error);
    }

    setAddingNewContact(false);
  };

  return { handleOnAddContact, addingNewContact, isValidEmail };
};
